// Раздел «Кто открывал» листа управления песней (волна F, задача 8, часть B).
//
// Что это. Владелец опубликовал запись и хочет знать, дошла ли она: кто её
// открывал, когда и сколько раз, и кого можно отсюда убрать. Форма взята у
// прототипа (docs/prototypes/2026-09-18-online-library.html, `buildOwnerPanel`,
// секция «Who has opened it»): кружок с буквой, почта над строкой «когда и
// сколько раз», действие справа.
//
// Модуль ЧИСТЫЙ, как соседние разделы листа: ни localStorage, ни `t()`, ни
// сети. Готовые подписи, готовые строки и обработчики приезжают параметрами;
// перевод, форматирование дат и сетевые клиенты остаются у library-view.ts.
//
// ── Три вещи, которые обязаны быть именно так ──────────────────────────────
//
// 1. РАЗДЕЛ — ТОЛЬКО ПРО ЗАПИСИ. У СЛОВ имён нет и не будет: решение схемы
//    0018 дословно — «кто открыл не пишется и не показывается, для команды из
//    четырёх человек это слежка». Раздел слов его уже соблюдает (показывает
//    только время), и этот раздел ему не противоречит: имена есть у аудио-
//    записи, потому что доступ к ней РАЗДАЁТСЯ поимённо и отзывается тоже
//    поимённо. Гейт стоит у вызывающего: раздела нет у песни без своих
//    записей.
//
// 2. ОТЗЫВ — ТОЛЬКО У ПРИГЛАШЁННЫХ ПО ССЫЛКЕ (`source === 'grant'`). У
//    участника команды доступ отнимает КОМАНДА, а не одна песня: убрать его
//    отсюда нечем, и кнопка, которая ничего не сделает, была бы ложью.
//    Гашёная — такая же ложь помягче: она обещает, что когда-нибудь
//    загорится. Вместо неё стоит ПОМЕТКА «Team», то есть ответ на вопрос
//    «почему тут ничего нет», а не молчание. Прототип говорит это прямо
//    (комментарий у `.op-remove`).
//
// 3. «НЕ ПРОЧИТАЛОСЬ» И «НИКОГО НЕТ» — РАЗНЫЕ СОСТОЯНИЯ. Отказ загрузки,
//    показанный как пустой список, читается как «вас никто не открывал», то
//    есть врёт ровно в том месте, ради которого раздел и открывают. Поэтому
//    `load` возвращает `null` на отказ, и раздел говорит об этом строкой
//    состояния, а не пустотой.

export interface SongSheetViewer {
  /** Ключ отзыва — тот же `userId`, что у `DELETE …/viewers/:userId`. */
  userId: string;
  email: string;
  /** Готовая строка «когда заходил и сколько раз». Форматирование дат
   *  (относительная подсказка, локаль, число раз) остаётся у вызывающего. */
  seen: string;
  /** Откуда доступ: `grant` — приглашён по ссылке (отзывается здесь),
   *  `team` — участник команды (отзывается только командой). */
  source: 'team' | 'grant';
}

export interface SongSheetViewersLabels {
  /** Надзаголовок секции — «Who has opened it». */
  heading: string;
  /** Никого нет. */
  empty: string;
  /** Список не прочитался — отдельное состояние, не пустота (пункт 3). */
  error: string;
  /** Подпись кнопки отзыва. */
  revoke: string;
  /** Пометка вместо кнопки у участника команды (пункт 2). */
  teamNote: string;
  /** Отзыв не прошёл. */
  revokeError: string;
}

export interface SongSheetViewersOpts {
  labels: SongSheetViewersLabels;
  /** Прочитать читателей песни. `null` — НЕ ПОЛУЧИЛОСЬ (пункт 3), пустой
   *  массив — получилось, и никого нет. */
  load: () => Promise<SongSheetViewer[] | null>;
  /** Отнять доступ у приглашённого по ссылке. Итог — булев: маршрут
   *  `DELETE /api/shares/:id/viewers/:userId` отвечает тем же. Без неё
   *  действия нет ни у кого. */
  revoke?: (userId: string) => Promise<boolean>;
}

export function buildSongSheetViewers(opts: SongSheetViewersOpts): HTMLElement {
  const { labels: L } = opts;

  const sec = document.createElement('section');
  sec.dataset.role = 'sheet-viewers';
  sec.className = 'lib-sheet-sec';

  const head = document.createElement('h3');
  head.dataset.role = 'sheet-viewers-head';
  head.className = 'lib-sheet-sec-head';
  head.textContent = L.heading;
  sec.appendChild(head);

  const listEl = document.createElement('div');
  listEl.dataset.role = 'sheet-viewers-list';
  listEl.className = 'lib-sheet-viewers';
  sec.appendChild(listEl);

  // Строка состояния — ЗА пределами перерисовываемого списка: причина отказа
  // обязана пережить перерисовку, иначе человек увидит вернувшегося читателя
  // без объяснения.
  const status = document.createElement('p');
  status.dataset.role = 'sheet-viewers-status';
  status.className = 'lib-sheet-sec-status';
  status.setAttribute('aria-live', 'polite');
  sec.appendChild(status);

  /** Известный список. `null` — ещё не прочитан или прочитать не удалось. */
  let people: SongSheetViewer[] | null = null;

  function paint(): void {
    listEl.innerHTML = '';
    if (people === null) return;
    if (people.length === 0) {
      const empty = document.createElement('p');
      empty.dataset.role = 'sheet-viewers-empty';
      empty.className = 'lib-sheet-sec-meta';
      empty.textContent = L.empty;
      listEl.appendChild(empty);
      return;
    }
    for (const v of people) listEl.appendChild(buildRow(v));
  }

  function buildRow(v: SongSheetViewer): HTMLElement {
    const row = document.createElement('div');
    row.dataset.role = 'sheet-viewer-row';
    row.dataset.userId = v.userId;
    row.className = 'lib-sheet-viewer';

    // Кружок с буквой — тот же приём, что у `.lib-avatar` в панели читателей:
    // в списке из восьми почт он и есть то, за что цепляется глаз.
    const av = document.createElement('span');
    av.className = 'lib-sheet-av';
    av.setAttribute('aria-hidden', 'true');   // имя уже прочитано почтой рядом
    av.textContent = (v.email.trim()[0] || '?').toLocaleUpperCase();
    row.appendChild(av);

    const main = document.createElement('span');
    main.className = 'lib-sheet-viewer-main';
    const mail = document.createElement('span');
    mail.className = 'lib-sheet-viewer-mail';
    mail.textContent = v.email;
    const seen = document.createElement('span');
    seen.className = 'lib-sheet-viewer-seen';
    seen.textContent = v.seen;
    main.appendChild(mail);
    main.appendChild(seen);
    row.appendChild(main);

    if (opts.revoke) row.appendChild(v.source === 'grant' ? buildRevoke(v) : buildTeamNote());

    return row;
  }

  function buildRevoke(v: SongSheetViewer): HTMLElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.role = 'sheet-viewer-revoke';
    btn.dataset.userId = v.userId;
    btn.className = 'lib-sheet-viewer-remove';
    btn.textContent = L.revoke;
    btn.addEventListener('click', () => {
      const revoke = opts.revoke;
      if (!revoke) return;
      btn.disabled = true;
      status.textContent = '';
      Promise.resolve(revoke(v.userId)).then(
        (ok) => {
          if (!ok) {
            // Читатель ОСТАЁТСЯ в списке, действие снова доступно, причина
            // названа. Убрать строку после отказа значило бы показать отзыв,
            // которого не было.
            btn.disabled = false;
            status.textContent = L.revokeError;
            return;
          }
          people = (people ?? []).filter((p) => p.userId !== v.userId);
          paint();
        },
        () => { btn.disabled = false; status.textContent = L.revokeError; },
      );
    });
    return btn;
  }

  /** Пометка вместо кнопки (пункт 2) — ответ, а не молчание. */
  function buildTeamNote(): HTMLElement {
    const note = document.createElement('span');
    note.dataset.role = 'sheet-viewer-team';
    note.className = 'lib-sheet-viewer-note';
    note.textContent = L.teamNote;
    return note;
  }

  Promise.resolve(opts.load()).then(
    (list) => {
      if (list === null) { status.textContent = L.error; return; }
      people = list;
      paint();
    },
    () => { status.textContent = L.error; },
  );

  return sec;
}
